import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BlogCard } from '../components/BlogCard';
import { ButtonLink } from '../components/Button';
import { CTASection } from '../components/CTASection';
import { PricingCard } from '../components/PricingCard';
import { Section, SectionHeading, Reveal } from '../components/Section';
import { ServiceCard } from '../components/ServiceCard';
import { featuredServices } from '../data/services';
import { hostingPricing, supportPricing } from '../data/pricing';
import { projects } from '../data/projects';
import { site } from '../data/site';
import { blogPosts } from '../lib/blog';
import { Icon } from '../lib/icons';
import { SEO } from '../lib/seo';

const trust = [
  { label: 'Massachusetts Based', icon: 'mapPin' },
  { label: 'Documentation First', icon: 'fileText' },
  { label: 'Long-Term Support', icon: 'lifeBuoy' },
  { label: 'Engineering Depth', icon: 'shield' }
];

const why = [
  {
    title: 'Local Massachusetts partner',
    body: 'Remote-friendly engineering with on-site context when a network closet, rack, property, or operations walkthrough matters.',
    icon: 'mapPin'
  },
  {
    title: 'No outsourced handoffs',
    body: 'You work with the people responsible for understanding the system, making decisions, and supporting the result.',
    icon: 'users'
  },
  {
    title: 'Documentation-first delivery',
    body: 'Runbooks, diagrams, admin notes, and handoff material are part of the work, not a last-minute extra.',
    icon: 'clipboard'
  },
  {
    title: 'Support after launch',
    body: 'Maintenance, managed hosting, retainers, and fractional engineering support keep systems useful over time.',
    icon: 'headphones'
  }
];

const process = [
  ['Discover', 'Understand the operation, current tools, constraints, risks, and the actual business outcome.'],
  ['Design', 'Map the workflow, data, integrations, support model, and the simplest version that solves the problem.'],
  ['Build', 'Develop the system with practical architecture, clear milestones, and communication throughout.'],
  ['Document', 'Create operating notes, diagrams, admin guidance, and maintenance expectations.'],
  ['Support', 'Keep the system running with hosting, monitoring, updates, and incremental improvements.']
];

export function Home() {
  const latestPosts = blogPosts.slice(0, 3);

  return (
    <>
      <SEO
        title="Massachusetts Software Development, Automation, and Infrastructure"
        description="MaytoMation builds practical software, automation, infrastructure, networking, AI systems, managed hosting, and long-term support for Massachusetts businesses."
      />
      <section className="relative overflow-hidden border-b border-brand-border bg-brand-background">
        <img
          src="/massachusetts-engineering-hero.png"
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-background via-brand-background/90 to-white/70" />
        <div className="absolute inset-0 dot-field opacity-35" />
        <div className="relative mx-auto grid min-h-[680px] max-w-7xl gap-10 px-5 py-16 sm:px-6 lg:grid-cols-[1fr_.86fr] lg:items-center lg:px-8 lg:py-20">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="inline-flex items-center gap-2 rounded-full border border-brand-teal/50 bg-white/80 px-3 py-1 text-xs font-extrabold uppercase tracking-[0.12em] text-brand-navy shadow-sm">
              <span className="h-2 w-2 rounded-full bg-brand-teal" />
              Now booking - Massachusetts
            </div>
            <h1 className="mt-7 max-w-4xl font-display text-5xl leading-[1.02] text-brand-dark sm:text-6xl lg:text-7xl">
              Practical software, automation, and infrastructure built for{' '}
              <span className="italic text-brand-navy">real operations.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-brand-navy/90">
              MaytoMation builds custom software, workflow automation, infrastructure, networking, AI systems, and
              managed hosting with clear documentation and long-term support.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <ButtonLink to="/contact" variant="dark">
                Request a Project
              </ButtonLink>
              <ButtonLink to="/services" variant="secondary">
                View Services
              </ButtonLink>
            </div>
            <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {trust.map((item) => (
                <div key={item.label} className="flex items-center gap-3 border-l border-brand-border pl-4 first:border-l-0 first:pl-0">
                  <Icon name={item.icon} className="h-6 w-6 text-brand-navy" />
                  <span className="text-sm font-bold text-brand-dark">{item.label}</span>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.aside
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.1 }}
            className="rounded-lg border border-white/10 bg-brand-dark p-6 text-white shadow-soft sm:p-8"
          >
            <div className="flex items-center gap-3 text-brand-teal">
              <Icon name="mapPin" className="h-7 w-7" />
              <span className="text-sm font-extrabold uppercase tracking-[0.14em]">Made in Massachusetts</span>
            </div>
            <h2 className="mt-6 font-display text-4xl leading-tight">Systems you can run without surprises.</h2>
            <p className="mt-4 text-sm leading-7 text-white/75">
              Clear scope, clear handoff, and ongoing support options so software and infrastructure stay maintainable.
            </p>
            <div className="mt-7 grid gap-3 sm:grid-cols-2">
              {[
                ['workflow', 'Operational automation', 'Intake to quote to schedule to invoice'],
                ['wifi', 'Infrastructure cleanup', 'WiFi, closets, racks, domains, docs'],
                ['brain', 'AI knowledge systems', 'Staff answers grounded in approved sources'],
                ['cloud', 'Managed hosting', 'Monitoring, backups, deployments, support']
              ].map(([icon, title, body]) => (
                <div key={title} className="rounded-lg border border-white/10 bg-white/[0.06] p-5">
                  <Icon name={icon} className="h-8 w-8 text-brand-teal" />
                  <h3 className="mt-4 text-base font-extrabold">{title}</h3>
                  <p className="mt-2 text-sm leading-6 text-white/70">{body}</p>
                </div>
              ))}
            </div>
            <blockquote className="mt-7 border-t border-white/10 pt-6">
              <Icon name="message" className="h-6 w-6 text-brand-teal" />
              <p className="mt-3 text-sm italic leading-7 text-white/75">
                "We needed practical systems and someone local who could actually support them. The documentation and
                follow-through made the difference."
              </p>
              <footer className="mt-5 text-sm font-bold text-white">Operations Director, Greater Boston</footer>
            </blockquote>
          </motion.aside>
        </div>
      </section>

      <Section>
        <div className="grid gap-10 lg:grid-cols-[.9fr_1.1fr] lg:items-start">
          <Reveal>
            <SectionHeading
              eyebrow="Local + Practical"
              title="Massachusetts-based technology support with engineering depth."
              body="MaytoMation is built for businesses that need practical systems, not buzzwords. The work is scoped, documented, and supported so it can survive real operations."
            />
          </Reveal>
          <div className="grid gap-4 sm:grid-cols-2">
            {why.map((item) => (
              <Reveal key={item.title}>
                <article className="rounded-lg border border-brand-border bg-white p-6 shadow-card">
                  <Icon name={item.icon} className="h-9 w-9 text-brand-teal" />
                  <h3 className="mt-5 text-lg font-extrabold text-brand-dark">{item.title}</h3>
                  <p className="mt-3 text-sm leading-7 text-brand-muted">{item.body}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      <Section className="bg-white">
        <SectionHeading
          eyebrow="Services"
          title="Software, automation, infrastructure, and support under one practical roof."
          body="Each engagement is designed around the business problem, the systems already in place, and the support model needed after launch."
        />
        <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {featuredServices.map((service) => (
            <Reveal key={service.title}>
              <ServiceCard {...service} />
            </Reveal>
          ))}
        </div>
      </Section>

      <Section>
        <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <SectionHeading
            eyebrow="Representative Work"
            title="Projects built around operational outcomes."
            body="These examples show the kind of practical software, automation, and infrastructure work MaytoMation plans and supports."
          />
          <ButtonLink to="/projects" variant="secondary" className="shrink-0">
            View Projects
          </ButtonLink>
        </div>
        <div className="mt-12 grid gap-5 lg:grid-cols-3">
          {projects.map((project) => (
            <Reveal key={project.title}>
              <article className="h-full rounded-lg border border-brand-border bg-white p-6 shadow-card">
                <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-teal">{project.category}</p>
                <h3 className="mt-4 text-xl font-extrabold text-brand-dark">{project.title}</h3>
                <p className="mt-4 text-sm font-bold text-brand-navy">Problem</p>
                <p className="mt-2 text-sm leading-7 text-brand-muted">{project.challenge}</p>
                <p className="mt-4 text-sm font-bold text-brand-navy">Solution</p>
                <p className="mt-2 text-sm leading-7 text-brand-muted">{project.solution}</p>
                <p className="mt-4 text-sm font-bold text-brand-navy">Outcome</p>
                <p className="mt-2 text-sm leading-7 text-brand-muted">{project.outcome}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section className="bg-brand-dark text-white">
        <SectionHeading
          eyebrow="Process"
          title="A clear path from problem to supported system."
          body="The process favors transparency, communication, maintainability, and documentation at every step."
        />
        <div className="mt-12 grid gap-4 md:grid-cols-5">
          {process.map(([title, body], index) => (
            <Reveal key={title}>
              <article className="h-full rounded-lg border border-white/10 bg-white/[0.06] p-6">
                <div className="flex h-11 w-11 items-center justify-center rounded-md bg-brand-teal text-lg font-extrabold text-brand-dark">
                  {index + 1}
                </div>
                <h3 className="mt-5 text-xl font-extrabold">{title}</h3>
                <p className="mt-3 text-sm leading-7 text-white/70">{body}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section id="hosting">
        <SectionHeading
          eyebrow="Hosting + Support"
          title="Monthly plans for systems that need ownership after launch."
          body="Hosting and support plans are matched to risk, response expectations, and the operational importance of the system."
        />
        <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {[...hostingPricing, ...supportPricing].map((plan) => (
            <PricingCard key={plan.title} {...plan} />
          ))}
        </div>
        <p className="mt-6 text-sm leading-7 text-brand-muted">
          Monthly plans are scoped after a short review of the system, risk profile, access model, and support
          expectations.
        </p>
      </Section>

      <Section className="bg-white">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <SectionHeading
            eyebrow="Field Notes"
            title="Latest practical writing."
            body="Readable guidance on automation, infrastructure, AI systems, hosting, networking, and maintainable software."
          />
          <ButtonLink to="/blog" variant="secondary" className="shrink-0">
            View Blog
          </ButtonLink>
        </div>
        <div className="mt-12 grid gap-5 lg:grid-cols-3">
          {latestPosts.map((post) => (
            <Reveal key={post.slug}>
              <BlogCard post={post} />
            </Reveal>
          ))}
        </div>
      </Section>

      <section className="border-y border-brand-border bg-brand-navy py-10 text-white">
        <div className="mx-auto grid max-w-7xl gap-6 px-5 sm:px-6 md:grid-cols-4 lg:px-8">
          {[
            ['building', 'Massachusetts Based', 'Local team. Local support.'],
            ['shield', 'Engineering You Can Trust', 'Reliable systems that scale with you.'],
            ['fileText', 'Clear Process Every Step', 'Discover to design to build to document to support.'],
            ['users', 'Built for Long-Term Success', 'Partnerships that keep operations running.']
          ].map(([icon, title, body]) => (
            <div key={title} className="border-white/10 md:border-l md:pl-6 md:first:border-l-0 md:first:pl-0">
              <Icon name={icon} className="h-8 w-8 text-brand-teal" />
              <h2 className="mt-4 font-display text-xl">{title}</h2>
              <p className="mt-2 text-sm leading-6 text-white/75">{body}</p>
            </div>
          ))}
        </div>
      </section>

      <CTASection />

      <section className="sr-only" aria-label="SEO summary">
        <h2>{site.name} Massachusetts technology company</h2>
        <p>
          Massachusetts software development, Massachusetts automation company, Massachusetts managed hosting,
          Massachusetts infrastructure services, Massachusetts networking company, custom software Massachusetts,
          contractor automation systems, business automation Massachusetts.
        </p>
        <Link to="/services">Services</Link>
      </section>
    </>
  );
}
